// IntroOverlay — full-screen pre-match comprehension card. Shown once
// (keyed by localStorage `signalclash.intro.seen`) at the start of the
// player's very first local match. Replaces the V1.6 in-match welcome
// tutorial step with a more prominent 6-point summary so the player
// understands the core loop (protect Core / capture Relays / earn Signal
// / build squads / build structures / defeat enemy Core) within the
// 30-60 s comprehension target called out in the V1.6 playtest report.
//
// Non-blocking: the match clock runs underneath, but the Recruit bot's
// 90 s warmup guarantees the player isn't punished for taking a few
// seconds to read. Dismissed by a single "Start Match" button or by
// pressing any key — both routes mark the intro as seen so the player
// never sees it again on the same browser. Network mode skips the
// overlay entirely (room is already running; first-time players land
// here rarely and the tutorial steps still apply).
//
// DOM-only, no sim hooks. The in-match Tutorial picks up immediately
// once `dispose()` removes this overlay's root.
//
// Touch / desktop split: the input modality affects the verbs used in
// the in-card hints ("Tap" vs "Left-click"). Computed once at first
// `beginIfFirstSession()` via `isTouchProfile()`.

import { analytics } from '../analytics/Analytics.js';
import { isTouchProfile } from '../input/Profile.js';
import { injectHudThemeStyles } from './hudThemeStyles.js';

const ROOT_ID = 'signalclash-intro';
const STYLE_ID = 'signalclash-intro-styles';
const STORAGE_KEY = 'signalclash.intro.seen';

interface Point {
  title: string;
  body: string;
}

export interface IntroOverlayHooks {
  // Called once when the overlay is about to be shown. ClientApp uses
  // this to pause the local sim so the match clock doesn't tick while
  // the player is reading. No-op if the overlay was already dismissed
  // in a prior session — the hook only fires when the modal actually
  // mounts.
  onShow?: () => void;
  // Called once when the overlay is dismissed (button, keypress, click,
  // or external dispose). Symmetric with onShow: ClientApp resumes the
  // sim here. Idempotent — repeated dismiss calls fire the hook at most
  // once per beginIfFirstSession.
  onDismiss?: () => void;
}

// V1.8 — auto-dismiss the briefing after this many seconds. CrazyGames
// analytics show many players linger on the overlay; matches end before
// they've engaged with the game. The countdown ticks visibly so readers
// still feel agency, but the match starts whether or not they click.
const AUTO_DISMISS_SECONDS = 8;

export class IntroOverlay {
  private root: HTMLDivElement | null = null;
  private keyHandler: ((ev: KeyboardEvent) => void) | null = null;
  private onDismissHook: (() => void) | null = null;
  // Guard so external dispose() during dismiss-animation cleanup doesn't
  // fire onDismiss a second time after the user already dismissed.
  private dismissed = false;
  // V4.1 — analytics anchors. shownAtMs starts on beginIfFirstSession;
  // intro_dismissed and intro_abandoned both compute elapsedMs against
  // it so the dashboard can split "read for 3 s → dismissed" from
  // "dismissed in 200 ms" (auto-bounce on accidental click) and from
  // "abandoned after 30 s" (left tab without dismissing).
  private shownAtMs = 0;
  // V1.8 — auto-dismiss timers. countdownInterval refreshes the visible
  // CTA label every second; autoDismissTimeout fires once when the count
  // reaches zero and routes through dismiss('auto') for analytics parity.
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private autoDismissTimeout: ReturnType<typeof setTimeout> | null = null;

  // Should be called by ClientApp on local-match start. Returns true if the
  // overlay actually mounted (caller should defer its post-start work until
  // onDismiss fires); false if the intro was already seen or the runtime
  // has no DOM (test environment) — caller can proceed immediately. The
  // boolean exists so ClientApp can branch between "show modal, then start
  // the match on dismiss" and "start the match right now" without a second
  // localStorage round-trip.
  beginIfFirstSession(hooks?: IntroOverlayHooks): boolean {
    if (typeof document === 'undefined') return false;
    if (this.alreadySeen()) return false;
    injectStyles();
    this.root = ensureRoot();
    this.dismissed = false;
    this.shownAtMs = Date.now();
    this.onDismissHook = hooks?.onDismiss ?? null;
    this.render();
    // Fire onShow only after we've committed to mounting the modal.
    hooks?.onShow?.();
    analytics.emit({ kind: 'intro_shown' });
    return true;
  }

  // Silent teardown (used by ClientApp when leaving the match before the
  // player has dismissed the overlay themselves — e.g. closing the tab,
  // returning to main menu). Does NOT fire onDismiss — the dismiss hook is
  // load-bearing in V1.7 (it kicks off host.start), and firing it during a
  // teardown would start a match the player just abandoned. Only the user-
  // initiated `dismiss()` path (click / keypress / CTA) fires the hook.
  dispose(): void {
    // V4.1 — if the modal was on screen at dispose-time, emit
    // intro_abandoned so the dashboard sees the "dismissed by leaving"
    // outcome. The `dismissed` guard isolates this from the user-dismiss
    // path (where intro_dismissed was already emitted).
    if (this.root !== null && !this.dismissed) {
      analytics.emit({
        kind: 'intro_abandoned',
        elapsedMs: Math.max(0, Date.now() - this.shownAtMs),
      });
    }
    // Clear the dismiss hook so any pending click/key racing dispose() can't
    // fire it after teardown either. The dismissed guard alone isn't enough
    // because user-dismiss flips it AFTER calling the hook.
    this.onDismissHook = null;
    this.dismissed = true;
    this.clearAutoDismiss();
    if (this.keyHandler !== null) {
      window.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
    if (this.root !== null) {
      this.root.remove();
      this.root = null;
    }
  }

  private clearAutoDismiss(): void {
    if (this.countdownInterval !== null) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.autoDismissTimeout !== null) {
      clearTimeout(this.autoDismissTimeout);
      this.autoDismissTimeout = null;
    }
  }

  private fireDismissHook(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    const hook = this.onDismissHook;
    this.onDismissHook = null;
    if (hook !== null) {
      try {
        hook();
      } catch (err) {
        // Containment: a throwing resume-hook must not strand the modal
        // half-removed. Mirror ClientApp's other listener-fanout style.
        // eslint-disable-next-line no-console
        console.warn('[IntroOverlay onDismiss] threw', err);
      }
    }
  }

  private render(): void {
    if (this.root === null) return;
    const touch = isTouchProfile();
    const verbSelect = touch ? 'Tap' : 'Left-click';
    const verbCommand = touch ? 'tap the ground' : 'right-click';
    const points: Point[] = [
      {
        title: 'Protect your Core',
        body: 'Your Core is the giant glowing structure on your side. If it falls, you lose.',
      },
      {
        title: 'Capture Relays for income',
        body: 'Send squads onto neutral Relays. Each captured Relay generates Signal — the resource you spend on everything.',
      },
      {
        title: 'Spend Signal on squads',
        body: `${verbSelect} your Core, then choose Recon, Assault, Engineer, or Shield. Each archetype plays differently.`,
      },
      {
        title: 'Build a Production Hub',
        body: 'When you can afford it, build a Hub from your Core panel. Each Hub adds a parallel production queue, so your squad output scales with your economy.',
      },
      {
        title: 'Push for the enemy Core',
        body: `${verbSelect} a squad, then ${verbCommand} where you want it to go. Destroy the enemy Core OR hold a Relay majority into Overtime to win.`,
      },
    ];

    this.root.innerHTML = `
      <div class="signalclash-intro__card" role="dialog" aria-labelledby="signalclash-intro-title">
        <div class="signalclash-intro__chip">SIGNAL CLASH · FIRST MATCH BRIEFING</div>
        <h2 id="signalclash-intro-title" class="signalclash-intro__title">How to win</h2>
        <ol class="signalclash-intro__list">
          ${points
            .map(
              (p, i) => `
            <li class="signalclash-intro__item">
              <div class="signalclash-intro__num">${i + 1}</div>
              <div class="signalclash-intro__text">
                <div class="signalclash-intro__item-title">${escapeHtml(p.title)}</div>
                <div class="signalclash-intro__item-body">${escapeHtml(p.body)}</div>
              </div>
            </li>`,
            )
            .join('')}
        </ol>
        <button class="signalclash-intro__cta" type="button"><span class="signalclash-intro__cta-label">START MATCH</span> <span class="signalclash-intro__cta-count">(${AUTO_DISMISS_SECONDS})</span></button>
        <div class="signalclash-intro__hint">Match auto-starts in <span class="signalclash-intro__hint-count">${AUTO_DISMISS_SECONDS}s</span> · press any key to skip</div>
      </div>
    `;
    this.root.style.display = 'flex';

    const cta = this.root.querySelector<HTMLButtonElement>('.signalclash-intro__cta');
    cta?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.dismiss('cta');
    });
    // Click anywhere on the backdrop also dismisses. Clicks inside the card
    // bubble unless they hit the CTA above (which stops propagation).
    this.root.addEventListener('click', () => this.dismiss('click'));

    this.keyHandler = (ev: KeyboardEvent): void => {
      // Ignore modifier-only keypresses so e.g. Shift held during reading
      // doesn't immediately dismiss the overlay.
      if (ev.key === 'Shift' || ev.key === 'Control' || ev.key === 'Alt' || ev.key === 'Meta') {
        return;
      }
      ev.preventDefault();
      this.dismiss('key');
    };
    window.addEventListener('keydown', this.keyHandler);

    let remaining = AUTO_DISMISS_SECONDS;
    const countSpan = this.root.querySelector<HTMLSpanElement>('.signalclash-intro__cta-count');
    const hintSpan = this.root.querySelector<HTMLSpanElement>('.signalclash-intro__hint-count');
    this.countdownInterval = setInterval(() => {
      remaining -= 1;
      if (remaining < 0) return;
      if (countSpan !== null) countSpan.textContent = `(${remaining})`;
      if (hintSpan !== null) hintSpan.textContent = `${remaining}s`;
    }, 1000);
    this.autoDismissTimeout = setTimeout(() => {
      this.dismiss('auto');
    }, AUTO_DISMISS_SECONDS * 1000);
  }

  private dismiss(method: 'cta' | 'click' | 'key' | 'auto'): void {
    this.clearAutoDismiss();
    // V4.1 — fire analytics BEFORE markSeen so a same-tick dispose race
    // can't drop the event. The elapsedMs split is the dashboard's primary
    // tool for distinguishing "read the briefing" from "smashed the
    // button" — 200 ms vs 3000 ms is the difference between confusion
    // and comprehension.
    analytics.emit({
      kind: 'intro_dismissed',
      elapsedMs: Math.max(0, Date.now() - this.shownAtMs),
      method,
    });
    this.markSeen();
    this.fireDismissHook();
    if (this.root !== null) {
      this.root.classList.add('signalclash-intro--out');
      const root = this.root;
      setTimeout(() => {
        if (root.parentElement !== null) root.remove();
      }, 240);
      this.root = null;
    }
    if (this.keyHandler !== null) {
      window.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
  }

  private alreadySeen(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private markSeen(): void {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage unavailable; the intro will replay next session.
    }
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: none;
  align-items: center;
  justify-content: center;
  background: rgba(4, 8, 14, 0.78);
  backdrop-filter: blur(4px);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  animation: signalclash-intro-in 240ms ease-out;
}
#${ROOT_ID}.signalclash-intro--out {
  animation: signalclash-intro-out 240ms ease-in forwards;
}
/* V2 Tempo polish: brought in line with the main-menu Commander
 * profile card — same radial dual-glow background, scanline overlay,
 * hex clip-path, and Orbitron headings. CTA mirrors the PLAY NOW
 * button's blue/red rim gradient. */
.signalclash-intro__card {
  width: min(92vw, 560px);
  max-height: 88vh;
  overflow-y: auto;
  padding: 24px 28px 22px;
  background:
    radial-gradient(ellipse 100% 100% at 0% 0%, rgba(30, 167, 255, 0.16), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 100%, rgba(255, 122, 90, 0.12), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.97), rgba(5, 9, 17, 0.99));
  border: 1px solid rgba(174, 240, 255, 0.32);
  border-radius: 0;
  clip-path: polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 44px rgba(30,167,255,0.12) inset,
    0 18px 50px rgba(0,0,0,0.7);
  color: #d4dae2;
  position: relative;
}
.signalclash-intro__card::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(174,240,255,0.04) 1px, transparent 1px);
  background-size: 100% 22px;
  opacity: 0.45;
  pointer-events: none;
}
.signalclash-intro__card > * { position: relative; z-index: 1; }
.signalclash-intro__chip {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  letter-spacing: 0.24em;
  color: #6fdcff;
  text-transform: uppercase;
  opacity: 0.9;
  margin-bottom: 8px;
}
.signalclash-intro__title {
  margin: 0 0 16px 0;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #aef0ff;
  text-shadow: 0 0 14px rgba(110,220,255,0.55), 0 0 28px rgba(30,167,255,0.35);
}
.signalclash-intro__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.signalclash-intro__item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.signalclash-intro__num {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  background: rgba(10, 19, 34, 0.85);
  border: 1px solid rgba(174, 240, 255, 0.40);
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
  color: #6fdcff;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 10px rgba(30,167,255,0.25) inset;
}
.signalclash-intro__text {
  flex: 1;
}
.signalclash-intro__item-title {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #eef4ff;
  margin-bottom: 3px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
}
.signalclash-intro__item-body {
  font-size: 12px;
  color: #a3b8d2;
  line-height: 1.5;
  font-family: 'Rajdhani', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.signalclash-intro__cta {
  display: block;
  width: 100%;
  margin-top: 20px;
  padding: 14px 18px;
  background: linear-gradient(180deg, rgba(30, 167, 255, 0.18), rgba(255, 42, 42, 0.10));
  color: #eef4ff;
  border: 1px solid rgba(174, 240, 255, 0.55);
  border-radius: 0;
  clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%);
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 0 18px rgba(30,167,255,0.30), 0 0 30px rgba(255,42,42,0.15);
  transition: color 150ms, box-shadow 150ms, transform 80ms;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px;
  text-shadow: 0 0 10px rgba(174,240,255,0.45);
}
.signalclash-intro__cta:hover {
  color: #aef0ff;
  box-shadow: 0 0 28px rgba(30,167,255,0.55), 0 0 40px rgba(255,42,42,0.25);
}
.signalclash-intro__cta:active { transform: scale(0.98); }
.signalclash-intro__hint {
  margin-top: 10px;
  text-align: center;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  color: #6a7d96;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
@keyframes signalclash-intro-in {
  0%   { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes signalclash-intro-out {
  0%   { opacity: 1; }
  100% { opacity: 0; }
}
@media (max-width: 540px), (pointer: coarse) {
  .signalclash-intro__card {
    width: min(94vw, 460px);
    padding: 16px 18px 14px;
  }
  .signalclash-intro__title { font-size: 18px; margin-bottom: 10px; }
  .signalclash-intro__num { width: 22px; height: 22px; flex-basis: 22px; font-size: 11px; }
  .signalclash-intro__item-title { font-size: 12px; }
  .signalclash-intro__item-body { font-size: 11px; }
  .signalclash-intro__cta { font-size: 12px; padding: 10px 12px; min-height: 44px; }
}
@media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
  .signalclash-intro__card { max-height: 92vh; padding: 10px 14px; }
  .signalclash-intro__title { font-size: 14px; margin-bottom: 6px; }
  .signalclash-intro__list { gap: 5px; }
  .signalclash-intro__item-title { font-size: 11px; }
  .signalclash-intro__item-body { font-size: 10px; line-height: 1.4; }
  .signalclash-intro__cta { margin-top: 8px; padding: 8px 12px; min-height: 36px; }
  .signalclash-intro__hint { margin-top: 4px; font-size: 9px; }
}
  `;
  document.head.appendChild(style);
  injectHudThemeStyles();
}
